import React from 'react';
import { User } from 'lucide-react';
import styled from 'styled-components';
import { fadeIn } from './styled';

const NotificationMenuContainer = styled.div`
  position: absolute;
  top: 56px;
  right: 0;
  width: 320px;
  max-height: 480px;
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 14px;
  box-shadow: 0 20px 45px rgba(1, 6, 17, 0.55);
  backdrop-filter: blur(14px);
  padding: 0;
  animation: ${fadeIn} 0.25s ease both;
  overflow: hidden;
  z-index: 100002;
  display: flex;
  flex-direction: column;

  @media (max-width: 576px) {
    right: -16px;
    width: 300px;
  }
`;

const NotificationMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const NotificationMenuTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #e5e7eb;
`;

const NotificationMenuButton = styled.button`
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #e5e7eb;
  }
`;

const NotificationList = styled.div`
  max-height: 360px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const NotificationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: background 0.2s ease;
  background: ${({ isRead }) => (isRead ? 'transparent' : 'rgba(0, 247, 210, 0.05)')};

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationImageWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #00f7d2;
`;

const NotificationImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NotificationIconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid #00f7d2;
  flex-shrink: 0;
  color: #e5e7eb;
`;

const NotificationContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NotificationName = styled.p`
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #00f7d2;
`;

const NotificationMessage = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: #e5e7eb;
  line-height: 1.4;
  word-wrap: break-word;
`;

const NotificationDate = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 2px;
`;

const NotificationBadge = styled.span`
  background: #00f7d2;
  color: #001018;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
`;

const EmptyNotification = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 0.9rem;
`;

const NotificationMenu = ({
  notifications,
  onMarkAllAsRead,
  menuRef,
}) => {
  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <NotificationMenuContainer ref={menuRef}>
      <NotificationMenuHeader>
        <NotificationMenuTitle>Notifications</NotificationMenuTitle>
        {unreadCount > 0 && (
          <NotificationMenuButton type="button" onClick={onMarkAllAsRead}>
            Mark all as read
          </NotificationMenuButton>
        )}
      </NotificationMenuHeader>
      <NotificationList>
        {notifications.length === 0 ? (
          <EmptyNotification>No notifications</EmptyNotification>
        ) : (
          notifications.map(notification => {
            const userImage = notification?.user_detail?.[0]?.image;
            const userName = notification?.user_detail?.[0]?.name || '';

            return (
              <NotificationItem
                key={notification._id}
                isRead={notification.is_read}
              >
                {userImage ? (
                  <NotificationImageWrap>
                    <NotificationImage src={userImage} alt={userName} />
                  </NotificationImageWrap>
                ) : (
                  <NotificationIconWrap>
                    <User size={20} />
                  </NotificationIconWrap>
                )}
                <NotificationContent>
                  {userName && <NotificationName>{userName}</NotificationName>}
                  <NotificationMessage>{notification.notification_message}</NotificationMessage>
                  <NotificationDate>
                    {notification.date}
                    {!notification.is_read && <NotificationBadge>New</NotificationBadge>}
                  </NotificationDate>
                </NotificationContent>
              </NotificationItem>
            );
          })
        )}
      </NotificationList>
    </NotificationMenuContainer>
  );
};

export default NotificationMenu;

